import { Link } from "react-router-dom";
import type { CategoryLetterGroup } from "@/types/directory";

interface LetterGroupProps {
  group: CategoryLetterGroup;
  /**
   * Maps an A–Z category name to its branded category slug. Names present here
   * link straight to the branded /category/<slug> page; every other name keeps
   * its keyword search link.
   */
  brandedSlugs?: Map<string, string>;
}

const isEmergency = (letter: string) => letter === "24";

export default function LetterGroup({ group, brandedSlugs }: LetterGroupProps) {
  const emergency = isEmergency(group.letter);
  const title = emergency ? "24x7 & Emergency Services" : `Starting with ${group.letter}`;

  return (
    <section id={`letter-${group.letter}`} className="scroll-mt-44">
      <div className="flex flex-wrap items-center gap-3 border-b border-background-200 pb-3">
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg font-heading text-lg font-extrabold ${
            emergency ? "bg-accent-500 text-foreground-950" : "bg-primary-500 text-background-50"
          }`}
        >
          {emergency ? <i className="ri-alarm-warning-line text-xl" /> : group.letter}
        </span>
        <h2 className="font-heading text-base font-bold text-foreground-950 md:text-lg">{title}</h2>
        <span className="ml-auto inline-flex items-center whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
          {group.categories.length} categories
        </span>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-0.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {group.categories.map((category) => {
          const brandedSlug = brandedSlugs?.get(category.name);
          const to = brandedSlug
            ? `/category/${brandedSlug}`
            : `/directory?keyword=${encodeURIComponent(category.name)}`;

          return (
            <Link
              key={category.name}
              to={to}
              title={
                brandedSlug
                  ? `Open the ${category.name} category page`
                  : `Search listings for ${category.name}`
              }
              className="group flex items-center justify-between gap-3 rounded-md px-3 py-2 transition-colors duration-200 hover:bg-secondary-50"
            >
              <span className="flex min-w-0 items-center gap-2 text-sm text-foreground-700 group-hover:text-primary-700">
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center ${
                    brandedSlug ? "text-accent-600" : "text-foreground-400 group-hover:text-accent-600"
                  }`}
                >
                  <i className={brandedSlug ? "ri-bookmark-3-line text-sm" : "ri-folder-3-line text-sm"} />
                </span>
                <span className="truncate">{category.name}</span>
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                {brandedSlug ? (
                  <span className="flex h-3.5 w-3.5 items-center justify-center text-accent-600">
                    <i className="ri-arrow-right-up-line text-sm" />
                  </span>
                ) : null}
                <span className="rounded-full bg-background-100 px-2 py-0.5 text-[11px] font-semibold text-foreground-500 group-hover:bg-accent-100 group-hover:text-accent-900">
                  {category.count}
                </span>
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}